import React, { useState, useEffect } from 'react';
import { connect } from 'react-redux';
import Col from "react-bootstrap/Col";
import Row from "react-bootstrap/Row";
import Container from "react-bootstrap/Container";
import forEach from 'lodash/forEach';
import map from 'lodash/map';
import size from 'lodash/size';

import format from 'date-fns/format';
import * as queryString from "query-string/index";
import { useHistory, useLocation } from "react-router";
import SearchResults from "../components/SearchResults";
import { getResultsSuccess, setResultsLoaded } from "../redux/results/action";
import { getVendors, setVendorsLoaded } from "../redux/vendors/action";
import LoadingVendors from "../components/LoadingVendors";
import SearchCarWidget from "../components/SearchCarWidget";
import FilterWidget from "../components/FilterWidget";
import { MAX_REQUEST_COUNT, MAX_REQUEST_PER_SECOND, DEFAULT_FILTERS, DEFAULT_CURRENCY, DEFAULT_RESIDENCY, DEFAULT_AGE } from "../constants/default";
import { useTranslation } from 'react-i18next';
import { getAttributor } from '../utils/activityTracking';
import { isMobile } from "react-device-detect";
import SmartFilterWidget from '../components/SmartFilterWidget';
import { djb2Hash } from '../utils/djb2Hash';
import { filterResults, normalizeFilterValue } from '../utils/filterUtils';
import { sortOffers } from '../utils/sortOffers';
import { useRecentSearches } from '../hooks/useRecentSearches';
import { parseDate } from '../utils/dateUtils';
import { trackEvent } from '../utils/analytics';
import { rewriteDeeplink, rewriteLink } from '../utils/deeplinkRewrites';

function Search({ dispatch, results, vendors, resultsLoaded, resultsLastCheck, vendorsLoaded, loading, dir, cur, currencies, vendorsLoading, vendorsLastCheck, locations, cid, user, residency: detectedResidency }) {
  // console.log('creating search container...')
  const attributor = getAttributor();
  const [params, setParams] = useState();
  const location = useLocation();
  const history = useHistory();
  const [counter, setCounter] = useState(0);
  const [filters, setFilters] = useState(DEFAULT_FILTERS);
  const [highlightOfferId, setHighlightOfferId] = useState();
  const [bookOfferId, setBookOfferId] = useState();
  const { t, i18n } = useTranslation();
  const [getVendorsIfNeeded, tryToGetVendorsIfNeeded] = useState(false);

  let isAirport = false
  let locationSlug = ''
  if (params && params.fromLocationCode) {
    const puLocations = Array.isArray(locations) && locations.filter(loc => loc.locationCode == params.fromLocationCode)
    const puLocation = puLocations.length > 0 && puLocations[puLocations.length - 1]
    isAirport = puLocation && puLocation.IATA && puLocation.IATA.length === 3
    locationSlug = puLocation ? `${puLocation?.country}-${puLocation?.city}-${puLocation?.IATA}`.toLocaleLowerCase() : ''
  }

  // Removing the param from the URL closes the booking modal (the effect below re-reads the URL)
  const closeBookingModal = () => {
    const current = queryString.parse(location.search);
    delete current.book;
    history.replace({ search: queryString.stringify(current) });
  };

  useEffect(() => {
    const params = queryString.parse(location.search);
    let { endDate, startDate, fromLocationCode, toLocationCode, age, residency, highlight, book, ...rest } = params;
    const ensuredResidency = residency || detectedResidency || DEFAULT_RESIDENCY;
    const ensuredAge = age || DEFAULT_AGE;
    // Offer to highlight (from an expired offer link). Only a decimal offerId is accepted; it is kept out of the API filters.
    const ensuredHighlight = typeof highlight === 'string' && /^\d{1,16}$/.test(highlight) ? highlight : undefined;
    setHighlightOfferId(ensuredHighlight);
    // Offer whose booking modal should open (from a links-service direct booking link). Same validation as highlight.
    const ensuredBook = typeof book === 'string' && /^\d{1,16}$/.test(book) ? book : undefined;
    setBookOfferId(ensuredBook);
    setParams({ endDate, startDate, fromLocationCode, toLocationCode, age: ensuredAge, residency: ensuredResidency, cur });

    // Normalize all filter values to arrays
    const normalizedFilters = {};
    Object.keys(rest).forEach(key => {
      normalizedFilters[key] = normalizeFilterValue(rest[key]);
    });

    // Merge with defaults so sortPrice is always present
    const mergedFilters = { ...DEFAULT_FILTERS, ...normalizedFilters };
    setFilters(mergedFilters);

    const normalizedSearch = {
      cur,
      endDate,
      startDate,
      fromLocationCode,
      toLocationCode,
      age: ensuredAge,
      residency: ensuredResidency,
      highlight: ensuredHighlight,
      book: ensuredBook,
      ...mergedFilters
    };

    const normalizedQuery = queryString.stringify(normalizedSearch);

    // Ensure we always persist a sortPrice in URL and avoid excessive replace loops
    if (normalizedQuery && location.search !== `?${normalizedQuery}`) {
      history.replace({ search: normalizedQuery });
    }
  }, [location.search, cur, detectedResidency]);

  const fetchResults = () => {

    if (!vendorsLoaded && counter < MAX_REQUEST_COUNT) {
      setTimeout(() => {
        tryToGetVendorsIfNeeded(true);
      }, 1000 / MAX_REQUEST_PER_SECOND);
    } else {
      dispatch(setVendorsLoaded(true));
      dispatch(setResultsLoaded(true));
    }
    // }
  };

  useEffect(() => {
    if (getVendorsIfNeeded) {
      tryToGetVendorsIfNeeded(false);
      if (!vendorsLoading && !vendorsLoaded && counter < MAX_REQUEST_COUNT) {
        if (params) {
          const payload = {
            puLocation: params.fromLocationCode,
            doLocation: params.toLocationCode,
            puDate: params.startDate,
            doDate: params.endDate,
            filter: JSON.stringify({
              age: params.age,
              cur: cur,
              residency: params.residency
            }),
          };

          const query = queryString.stringify(payload, { encode: false });
          dispatch(getVendors({ query }));
        }
      }

      setCounter(counter + 1);
    }
  }, [getVendorsIfNeeded])

  useEffect(() => {
    if (params && !vendorsLoaded && !vendorsLoading) {
      fetchResults();
    }
  }, [params, counter, vendorsLastCheck]); // eslint-disable-line

  useEffect(() => {
    // tracking
    // tood: track age
    if (params && params.startDate && params.endDate) {
      const puDate = parseDate(params.startDate);
      const doDate = parseDate(params.endDate);
      const daysToTrip = Math.round((puDate - Date.now()) / (60 * 60 * 24 * 1000));
      const tripDays = Math.round((doDate - puDate) / (60 * 60 * 24 * 1000));
      trackDaysToPickup(daysToTrip);
      trackRentalDuration(tripDays);
    }
    // originally we were pulling cid here. it is moved to app level because I need it on homepage too.
  }, [params]); // eslint-disable-line


  useEffect(() => {
    if (vendorsLoaded) { // Vendors loaded
      let items = [];

      forEach(vendors, (group, key) => {

        if (group) {
          items = [...items, ...map(group.data, item => ({ ...item, key }))]
        }
      });

      // Convert rating from 1-5 scale to 1-10 scale once when data is first loaded
      items.forEach(item => {
        if (item.localSupplier && item.localSupplier.googlePlace) {
          item.localSupplier.googlePlace.rating *= 2;
        }
      });

      setTimeout(() => {
        dispatch(getResultsSuccess(items));
        setCounter(0);
      }, 1000 / MAX_REQUEST_PER_SECOND);
    }
  }, [vendorsLoaded]);

  const trackDaysToPickup = (days) => {
    trackEvent('Days to pickup', {
      'event_category': 'Travel Length',
      // 'event_label': days,
      'value': days
    });
  }

  const trackRentalDuration = (days) => {
    trackEvent('Rental duration', {
      'event_category': 'Travel Length',
      // 'event_label': days,
      'value': days
    });
  }

  const onSubmit = fields => {
    const params = {
      startDate: `${format(fields.startDate, 'dd/MM/yyyy')}T${fields.hoursFrom}:${fields.minsFrom}`,
      endDate: `${format(fields.endDate, 'dd/MM/yyyy')}T${fields.hoursTo}:${fields.minsTo}`,
      fromLocationCode: fields.fromLocation.locationCode,
      toLocationCode: fields.toLocation.locationCode,
      age: fields.age || DEFAULT_AGE,
      cur: cur,
      residency: fields.residency || detectedResidency || DEFAULT_RESIDENCY,
      ...DEFAULT_FILTERS,
    };
    setParams(params);
    setFilters(DEFAULT_FILTERS); // clear filters on every search

    dispatch(setVendorsLoaded(false));
    dispatch(setResultsLoaded(false));
    // no need to apply lang because path is "search" so it remains on whatever lang prefix we are at anyway
    history.push({
      search: `${queryString.stringify(params)}`
    })
  };

  // Starting 08/09/20
  // onApplyFilters is obsolete as long as onFilterChange act on any filter change
  // const onApplyFilters = () => {
  //   setQueryFilters(filters);

  //   history.push({
  //     pathname: routes.SEARCH,
  //     search: `${queryString.stringify({...params, ...filters})}`
  //   })
  // };

  const onClearFilters = filters => {
    setFilters(DEFAULT_FILTERS);

    // no need to apply lang because path is "search" so it remains on whatever lang prefix we are at anyway
    history.push({
      search: `${queryString.stringify({ ...params, ...DEFAULT_FILTERS })}`
    })
  };

  const onFilterChange = values => {
    const newFilters = { ...filters, ...values };
    setFilters(newFilters);

    history.push({
      search: `${queryString.stringify({ cur, ...params, ...newFilters })}`
    })
  };

  const convertCurrency = (sourceCurrency, targetCurrency, sourceValue) => {
    return sourceValue * currencies[targetCurrency] / currencies[sourceCurrency]
  }

  // generic query filters are automatically dealt with. Here we extract the filters with non trivial logic
  const sortResults = (data) => sortOffers(data, filters.sortPrice);

  const manipulateResults = (data) => {
    return data.map(item => {
      const newItem = { ...item }

      // --- Normalize luggageLiters field ---
      if (!newItem.spec) newItem.spec = {};
      let luggageLiters = 4000; // default to a very high number if no info available so that these cars appear at the end of the list
      if (newItem.spec.maxLuggageCapacity && newItem.spec.maxLuggageCapacity !== '?' && !isNaN(Number(newItem.spec.maxLuggageCapacity))) {
        luggageLiters = parseInt(newItem.spec.maxLuggageCapacity, 10);
      } else if (newItem.spec.luggage && !isNaN(Number(newItem.spec.luggage))) {
        luggageLiters = parseInt(newItem.spec.luggage, 10) * 220;
      } else if (newItem.spec.luggageAgg && newItem.spec.luggageAgg.median && !isNaN(Number(newItem.spec.luggageAgg.median))) {
        luggageLiters = parseInt(newItem.spec.luggageAgg.median, 10);
      }
      newItem.spec.luggageLiters = luggageLiters;

      // Legacy offer ID (djb2 over the original 9-field key), so favourites and shared links made before the
      // server-side offerId still match. Retire in January 2027: remove this block and newItem.legacyId.
      const legacyOfferKey = [
        newItem.model || '',
        newItem.group || '',
        newItem.localSupplier?.displayName || '',
        newItem.terms?.kmLimit || '',
        newItem.terms?.basicCoverageIncluded || '',
        newItem.terms?.excessRefundIncluded || '',
        newItem.payment?.payNow != newItem.payment?.total,
        newItem.key || '',
        newItem.terms?.zeroExcess || false,
      ].join('|');
      newItem.legacyId = djb2Hash(legacyOfferKey).toString();

      // Offer ID is computed by the search API (offerId.js) and excludes price and deeplink. Offers cached before that
      // deploy have no offerId yet, so they fall back to the legacy ID until the cache refreshes.
      newItem.id = newItem.offerId || newItem.legacyId;

      // adding tracking and proper lang/currency to deeplinks
      if (newItem.deeplink) {
        newItem.deeplink = rewriteDeeplink(newItem.deeplink, { lang: i18n.language, cur, attributor, cid });
      }
      // offers from the links service go through /go, which applies the same params on the partner side
      if (newItem.link) {
        newItem.link = rewriteLink(newItem.link, { lang: i18n.language, cur, residency: params.residency, attributor, cid });
      }

      // manipulate currency
      if (newItem.payment.total && newItem.payment.currency) {
        newItem.payment.renderTotal = convertCurrency(newItem.payment.currency, cur, newItem.payment.total)
        newItem.payment.renderPayNow = convertCurrency(newItem.payment.currency, cur, newItem.payment.payNow)
        newItem.payment.renderCurrency = cur
      }

      // consolidate from DesktopItem and MobileItem logic
      if (newItem.terms && newItem.terms.currency && typeof newItem.terms.currency !== 'object') {
        if (typeof newItem.terms.excess !== 'undefined') {
          newItem.terms.zeroExcess = newItem.terms.excess === 0;
          // item.terms.excess = item.terms.excess.toLocaleString(locale, { style: 'currency', currency: item.terms.currency });
        }
        // if (typeof item.terms.deposit !== 'undefined') {
        //   item.terms.deposit = item.terms.deposit.toLocaleString(locale, { style: 'currency', currency: item.terms.currency });
        // }
        // if (typeof item.terms.noShowCost !== 'undefined') {
        //   item.terms.noShowCost = item.terms.noShowCost.toLocaleString(locale, { style: 'currency', currency: item.terms.currency });
        // }
        // if (typeof item.terms.excess !== 'undefined') {
        //   item.terms.excess = item.terms.excess.toLocaleString(locale, { style: 'currency', currency: item.terms.currency });
        // }
      }

      // add vat note for israel locations except of eilat
      if (newItem.puLocation < 100 && newItem.puLocation !== 4 && newItem.puLocation !== 18) {
        if (params.residency.toLowerCase() === 'il') {
          // For Israeli residents, always include VAT
          if (!newItem.payment.vatIncluded) {
            // If not already included, multiply by 1.18
            newItem.payment.renderTotal *= 1.18;
            // newItem.payment.renderPayNow *= 1.18; usually VAT to be paid at pickup, so we don't modify payNow
          }
          newItem.payment.vatNote = t("vatIncludedText");
          newItem.payment.showVatIncludedNote = true;
        } else {
          // For non-Israelis, show the existing logic
          if (newItem.payment.vatIncluded) {
            newItem.payment.vatNote = t("vatIncludedText")
          } else {
            newItem.payment.vatNote = t("vatNotIncludedText")
          }
        }
      }

      // calculate branch rating (was in Rating.js)
      if (newItem.localSupplier && newItem.localSupplier.googlePlace) {
        const googlePlace = newItem.localSupplier.googlePlace;
        if (googlePlace.rating >= 6 && googlePlace.userRatingsTotal >= 30) {
          googlePlace.ratingString = "recommended";
        } else {
          googlePlace.ratingString = "lowRating";
        }
      }

      return newItem
    });
  };

  // const prepareResults = data => {
  //   let result = filterResults(data);
  //   result = sortResults(result);
  //   result = manipulateResults(result);
  //   return result;
  // };

  // Split prepareResults into filterResults, sortResults, and manipulateResults
  // console.log(`preping results...`)
  const allResults = sortResults(manipulateResults(results))
  const filteredResults = filterResults(allResults, filters, isAirport, params?.startDate, params?.endDate)

  // console.log('before render', filters)
  return (

    <Container fluid className="search-wrapper flex-fill px-0 px-lg-3 min-vh-100">
      {!resultsLoaded && <LoadingVendors dir={dir} data={vendors} progress={Math.round(counter / MAX_REQUEST_COUNT * 100)} />}

      {resultsLoaded && (
        <Row noGutters={true}>
          <Col md={4} sm={12} style={{ maxWidth: "500px" }}>
            <SearchCarWidget dir={dir} onSubmit={onSubmit} params={params} minimized={true} />
            {size(allResults) > 0 ? (
              isMobile ? null : (
                <FilterWidget
                  dir={dir}
                  data={allResults}
                  activeFilters={filters}
                  onChange={onFilterChange}
                  onClear={onClearFilters}
                  isAirport={isAirport}
                  locationSlug={locationSlug}
                  pickupDate={params?.startDate}
                  dropoffDate={params?.endDate} />
              )
            ) : ""}
          </Col>
          <Col md={8} sm={12}>
            <SmartFilterWidget
              dir={dir}
              data={allResults}
              activeFilters={filters}
              isAirport={isAirport}
              onChange={onFilterChange}
              onClear={onClearFilters}
              pickupDate={params?.startDate}
              dropoffDate={params?.endDate} />
            {size(allResults) > 0
              ? <SearchResults 
                  dir={dir} 
                  data={filteredResults} 
                  allResults={allResults} 
                  highlightOfferId={highlightOfferId}
                  bookOfferId={bookOfferId}
                  onBookClose={closeBookingModal}
                  onClear={onClearFilters} 
                  cid={cid} 
                  attributor={attributor} 
                  isAirport={isAirport} 
                  locationSlug={locationSlug} 
                  params={params}
                  locations={locations}
                  activeFilters={filters}
                />
              :
              <div className="results-section px-md-4">
                <Row noGutters={true} className="item-wrapper rounded">
                  <Col>
                    {t("noResults")}
                  </Col>
                </Row>
              </div>
            }
          </Col>
        </Row>
      )}
    </Container>
  );
}

const mapStateToProps = ({ vendors, results, ui, currencies, locations, prefs, user }) => ({
  vendors: vendors.data,
  vendorsLoaded: vendors.loaded,
  vendorsLoading: vendors.loading,
  vendorsLastCheck: vendors.lastCheck,
  results: results.data,
  resultsLastCheck: results.lastCheck,
  resultsLoaded: results.loaded,
  loading: ui.loading,
  dir: ui.direction,
  cur: prefs.cur || DEFAULT_CURRENCY, // Get currency from prefs with fallback
  residency: prefs.residency, // Get detected residency from prefs
  currencies: currencies.data,
  locations: locations.data,
  user: user.user, // Add user to props
});

export default connect(mapStateToProps)(Search);
